import React, { useState, useEffect, useRef } from 'react';
import { searchProducts, getStores, getCategoryIcon, getCategoryGradient } from '../utils/db';
import PriceComparison from '../components/PriceComparison';
import ProductCardGrid from '../components/ProductCardGrid';
import RequestModal from '../components/requests/RequestModal';
import { AISLES, getSubcategoryDisplay } from '../utils/taxonomy';
import { logSearchMiss, logProductRequest } from '../components/requests/requestsHelpers';
import './ExplorePage.css';
import './BrowsePage.css'; // shared lv-* sheet styles

// ── ExplorePage ──────────────────────────────────────────────────────────────
// Three visible layers: Search → Mode → Results
// Aisle chips + shortcut row sit above the grid.
// All advanced filter/sort controls are in sheets.

// ── DEV: local icon overrides for testing before Firestore iconUrl is populated ──
// Remove once product iconUrl fields are set in Firestore.
function getIconOverride(product) {
  const name = (product.name || '').toLowerCase();
  if (name.includes('milk'))                                return '/product-icons/milk.png';
  if (name.includes('grated') && name.includes('cheese'))   return '/product-icons/grated-cheese.png';
  if (name.includes('sliced') && name.includes('cheese'))   return '/product-icons/sliced-cheese.png';
  if (name.includes('cheese'))                              return '/product-icons/block-cheese.png';
  if (name.includes('olive oil'))                           return '/product-icons/olive-oil.png';
  if (name.includes('spaghetti'))                           return '/product-icons/spaghetti.png';
  if (name.includes('egg'))                                 return '/product-icons/eggs.png';
  if (name.includes('baked bean'))                          return '/product-icons/baked-beans.png';
  if (name.includes('unsalted') && name.includes('butter')) return '/product-icons/unsalted-butter.png';
  if (name.includes('butter'))                              return '/product-icons/salted-butter.png';
  if (name.includes('brown') && name.includes('bread'))     return '/product-icons/brown-bread.png';
  if (name.includes('seeded') && name.includes('bread'))    return '/product-icons/seeded-bread.png';
  if (name.includes('white') && name.includes('bread'))     return '/product-icons/white-bread.png';
  if (name.includes('banana'))                              return '/product-icons/bananas.png';
  return null;
}

// ── Constants ────────────────────────────────────────────────────────────────

const POPULAR_PRODUCT_NAMES = [
  'Semi-Skimmed Milk',
  'White Bread',
  'Free Range Eggs',
  'Spaghetti',
  'Olive Oil',
  'Basmati Rice',
  'Butter',
];

const FREQUENT_THRESHOLD = 3; // views ≥ 3 → "Buy again"

const RANGES = [
  { id: 'basic',    label: 'Basic' },
  { id: 'standard', label: 'Standard' },
  { id: 'premium',  label: 'Premium' },
  { id: 'organic',  label: 'Organic' },
];

const PACK_SIZES = [
  { id: 'small',  label: 'Small' },
  { id: 'medium', label: 'Medium' },
  { id: 'large',  label: 'Large' },
];

const SORT_OPTIONS = [
  { id: 'recommended',  label: 'Recommended',  sub: 'Standard first, then by value' },
  { id: 'best-value',   label: 'Best value',    sub: 'Cheapest unit price first' },
  { id: 'az',           label: 'A–Z',           sub: 'Alphabetical order' },
  { id: 'lowest-price', label: 'Lowest price',  sub: 'Cheapest absolute price first' },
];

// Sheet filters (aisle lives on the main screen, not in the sheet)
const EMPTY_FILTERS = {
  subcategory: null,
  range:       null,   // maps to tier
  stores:      [],
  ownBrand:    false,
  aldiMatch:   false,
  packSizes:   [],
  recentOnly:  false,
};

const TIER_ORDER = { standard: 0, basic: 1, premium: 2, organic: 3 };
const getTierPriority = (tier) => TIER_ORDER[(tier || '').toLowerCase()] ?? 4;

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseSizeToBase(sizeStr) {
  if (!sizeStr) return 0;
  const match = String(sizeStr).toLowerCase().match(/^([\d.]+)\s*(g|kg|ml|l\b|litre|litres)/);
  if (!match) return 0;
  const val  = parseFloat(match[1]);
  const unit = match[2].replace(/s$/, '');
  switch (unit) {
    case 'kg': case 'litre': case 'l': return val * 1000;
    case 'g':  case 'ml':              return val;
    default: return 0;
  }
}

function getUnitPrice(product) {
  const valid = product.prices?.filter(x => x.price > 0);
  if (!valid?.length) return Infinity;
  const cheapest = Math.min(...valid.map(x => x.price));
  const base = parseSizeToBase(product.size);
  if (!base) return Infinity;
  return (cheapest / base) * 100;
}

function getMinPrice(product) {
  const valid = product.prices?.filter(x => x.price > 0);
  return valid?.length ? Math.min(...valid.map(x => x.price)) : Infinity;
}

function normStoreId(storeId) {
  return storeId?.replace(/^store_/, '') ?? storeId;
}

// ── ShortcutCard — compact horizontal card ────────────────────────────────────
function ShortcutCard({ product, stores, onSelect }) {
  const sorted = (product.prices || [])
    .filter(p => p.price > 0)
    .sort((a, b) => a.price - b.price);
  const cheapest = sorted[0];
  const iconUrl = product.iconUrl || getIconOverride(product);

  return (
    <button
      className="ep-shortcut-card"
      onClick={() => onSelect(product)}
      aria-label={`View ${product.name}`}
    >
      {/* Fallback ladder: iconUrl (Firestore) → local override → emoji + gradient */}
      <div
        className="ep-shortcut-img"
        style={iconUrl ? undefined : { background: getCategoryGradient(product.category) }}
        aria-hidden="true"
      >
        {iconUrl
          ? <img src={iconUrl} alt="" className="ep-shortcut-photo" />
          : <span>{getCategoryIcon(product.category)}</span>
        }
      </div>
      <div className="ep-shortcut-body">
        <p className="ep-shortcut-name">{product.name}</p>
        {cheapest && (
          <p className="ep-shortcut-price">£{cheapest.price.toFixed(2)}</p>
        )}
      </div>
    </button>
  );
}

// Tick for filter sheet checkboxes
const Tick = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2 6.5 5.5 10 11 3"/>
  </svg>
);

// Check for sort sheet active option
const Check = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2 8 6 12 14 4"/>
  </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

function ExplorePage({ onAddToBasket }) {

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [stores, setStores]                   = useState([]);
  const [allProducts, setAllProducts]         = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ── View history (localStorage) ──────────────────────────────────────────────
  // Each entry: { productId, name, count, viewedAt }
  const [viewHistory, setViewHistory] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('chopmyshop-view-history') || '[]');
    } catch { return []; }
  });

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const [sortMode, setSortMode] = useState('recommended');

  // ── Search ───────────────────────────────────────────────────────────────────
  const [listQuery, setListQuery] = useState('');

  // ── Aisle (main-screen chip — instant, not in sheet) ─────────────────────────
  const [selectedAisle, setSelectedAisle] = useState(null);

  // ── Grid animation key — increment on aisle change to trigger fade ───────────
  const [gridKey, setGridKey] = useState(0);

  // ── Sheets ───────────────────────────────────────────────────────────────────
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen]     = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // ── Request product ───────────────────────────────────────────────────────────
  const [showRequestModal, setShowRequestModal]   = useState(false);
  const [requestSubmitted, setRequestSubmitted]   = useState(false);

  // Ref holds the latest filteredProducts length synchronously for the effect below
  const filteredCountRef = useRef(0);

  // ── Filters (sheet: subcategory, range, store, more) ─────────────────────────
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [pendingFilters, setPendingFilters] = useState({ ...EMPTY_FILTERS });

  // ── Data fetching ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getStores().then(setStores);
  }, []);

  useEffect(() => {
    searchProducts('').then(products => {
      setAllProducts(products);
      // One card per productId family — standard tier as representative.
      // allProducts retains every tier+variant doc for PriceComparison siblings.
      const PRIORITY = ['standard', 'basic', 'premium', 'organic', 'brand'];
      const seen = new Map();
      products.forEach(p => {
        const key = p.productId || p.id;
        if (!seen.has(key)) {
          seen.set(key, p);
        } else {
          const ePri = PRIORITY.indexOf(seen.get(key).tier ?? '');
          const nPri = PRIORITY.indexOf(p.tier ?? '');
          if (nPri !== -1 && (nPri < ePri || ePri === -1)) seen.set(key, p);
        }
      });
      setDisplayProducts([...seen.values()]);
    }).catch(err => console.error('ExplorePage: error loading products', err));
  }, []);

  // ── Search miss logging ───────────────────────────────────────────────────────
  // Fires silently when user has typed a term that returned zero results.
  // Uses a ref so the effect doesn't need filteredProducts in its dep array
  // (filteredProducts is re-computed every render; the ref is updated below).
  useEffect(() => {
    const query = listQuery.trim();
    if (query && filteredCountRef.current === 0) {
      logSearchMiss(query);
    }
  }, [listQuery]);

  // Reset the "submitted" confirmation whenever the search term changes
  useEffect(() => {
    setRequestSubmitted(false);
  }, [listQuery]);

  // ── View tracking ─────────────────────────────────────────────────────────────
  const handleSelectProduct = (product) => {
    const pid = product.productId || product.id;
    setViewHistory(prev => {
      const existing = prev.find(v => v.productId === pid);
      const updated = existing
        ? prev.map(v => v.productId === pid
            ? { ...v, count: v.count + 1, viewedAt: Date.now() }
            : v)
        : [...prev, { productId: pid, name: product.name, count: 1, viewedAt: Date.now() }];
      try { localStorage.setItem('chopmyshop-view-history', JSON.stringify(updated)); } catch {}
      return updated;
    });
    setSelectedProduct(product);
  };

  // ── Shortcut row derivation ───────────────────────────────────────────────────
  const getShortcutRow = () => {
    if (displayProducts.length === 0) return { title: 'Popular comparisons', products: [] };

    // Buy again — products viewed ≥ FREQUENT_THRESHOLD times
    const frequent = viewHistory
      .filter(v => v.count >= FREQUENT_THRESHOLD)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    if (frequent.length >= 3) {
      const products = frequent
        .map(v => displayProducts.find(p => p.id === v.productId))
        .filter(Boolean);
      return { title: 'Buy again', products };
    }

    // Recently viewed
    if (viewHistory.length > 0) {
      const recent = [...viewHistory]
        .sort((a, b) => b.viewedAt - a.viewedAt)
        .slice(0, 8);
      const products = recent
        .map(v => displayProducts.find(p => p.id === v.productId))
        .filter(Boolean);
      if (products.length > 0) return { title: 'Recently viewed', products };
    }

    // Popular comparisons — match static names against catalogue
    const products = POPULAR_PRODUCT_NAMES
      .map(name =>
        displayProducts.find(p =>
          p.name?.toLowerCase().includes(name.toLowerCase())
        )
      )
      .filter(Boolean);
    return { title: 'Popular comparisons', products };
  };

  const shortcutRow = getShortcutRow();

  // ── Subcategory derivation (stable — uses full displayProducts) ───────────────
  const getSubcategoriesForAisle = (aisleId) => {
    if (!aisleId) return [];
    const aisle = AISLES.find(a => a.id === aisleId);
    if (!aisle) return [];
    const subcatSet = new Set();
    displayProducts
      .filter(p => aisle.categories.includes((p.category || '').toLowerCase()))
      .forEach(p => { if (p.subcategory) subcatSet.add(p.subcategory); });
    return [...subcatSet].sort();
  };

  // ── Aisle handler — "All" chip sets null; clicking already-active aisle is no-op ──
  const handleAisleSelect = (aisleId) => {
    if (aisleId === selectedAisle) return; // already selected
    setSelectedAisle(aisleId);
    // Reset subcategory whenever the aisle changes
    setAppliedFilters(f => ({ ...f, subcategory: null }));
    setPendingFilters(f => ({ ...f, subcategory: null }));
    setGridKey(k => k + 1);
  };

  // ── Filter sheet handlers ─────────────────────────────────────────────────────
  const openFilterSheet = () => {
    setPendingFilters({
      ...appliedFilters,
      stores:    [...appliedFilters.stores],
      packSizes: [...appliedFilters.packSizes],
    });
    setShowMoreOptions(false);
    setFilterSheetOpen(true);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...pendingFilters });
    setFilterSheetOpen(false);
    setGridKey(k => k + 1);
  };

  const clearAll = () => {
    setSelectedAisle(null);
    setAppliedFilters({ ...EMPTY_FILTERS });
    setPendingFilters({ ...EMPTY_FILTERS });
    setListQuery('');
    setGridKey(k => k + 1);
  };

  const togglePendingStore = (storeId) => {
    setPendingFilters(f => ({
      ...f,
      stores: f.stores.includes(storeId)
        ? f.stores.filter(id => id !== storeId)
        : [...f.stores, storeId],
    }));
  };

  const handleToggleMoreOptions = () => {
    setShowMoreOptions(v => {
      const next = !v;
      if (next) {
        setTimeout(() => {
          const scroll = document.querySelector('.lv-filter-scroll');
          if (scroll) scroll.scrollTop = scroll.scrollHeight;
        }, 50);
      }
      return next;
    });
  };

  // ── Active filter count (for "Filters · 2 ↓") ────────────────────────────────
  const activeFilterCount = [
    appliedFilters.subcategory !== null,
    appliedFilters.range !== null,
    appliedFilters.stores.length > 0,
    appliedFilters.ownBrand,
    appliedFilters.aldiMatch,
    appliedFilters.packSizes.length > 0,
    appliedFilters.recentOnly,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;
  const isSortModified   = sortMode !== 'recommended';

  // ── Filtering + sorting ───────────────────────────────────────────────────────
  const getFilteredProducts = (base) => {
    let filtered = [...base];

    if (listQuery.trim()) {
      const q = listQuery.toLowerCase();
      filtered = filtered.filter(p => p.name?.toLowerCase().includes(q));
    }

    if (selectedAisle) {
      const aisle = AISLES.find(a => a.id === selectedAisle);
      if (aisle) {
        filtered = filtered.filter(p =>
          aisle.categories.includes((p.category || '').toLowerCase())
        );
      }
    }

    if (appliedFilters.subcategory) {
      filtered = filtered.filter(p => p.subcategory === appliedFilters.subcategory);
    }

    if (appliedFilters.range) {
      filtered = filtered.filter(p =>
        (p.tier || '').toLowerCase() === appliedFilters.range
      );
    }

    if (appliedFilters.stores.length > 0) {
      filtered = filtered.filter(p =>
        p.prices?.some(pr =>
          appliedFilters.stores.includes(normStoreId(pr.storeId)) && pr.price > 0
        )
      );
    }

    if (appliedFilters.ownBrand) {
      filtered = filtered.filter(p => p.ownBrand === true);
    }

    if (appliedFilters.aldiMatch) {
      filtered = filtered.filter(p =>
        p.prices?.some(pr => normStoreId(pr.storeId) === 'aldi' && pr.price > 0)
      );
    }

    if (appliedFilters.packSizes.length > 0) {
      filtered = filtered.filter(p => {
        const sz = parseSizeToBase(p.size);
        if (!sz) return false;
        return appliedFilters.packSizes.some(ps => {
          if (ps === 'small')  return sz < 300;
          if (ps === 'medium') return sz >= 300 && sz <= 1000;
          if (ps === 'large')  return sz > 1000;
          return false;
        });
      });
    }

    if (appliedFilters.recentOnly) {
      const cutoff = Date.now() / 1000 - 7 * 24 * 60 * 60;
      filtered = filtered.filter(p =>
        p.prices?.some(pr => {
          const ts = pr.updatedAt?.seconds || pr.updatedAt || 0;
          return ts > cutoff;
        })
      );
    }

    if (sortMode === 'recommended') {
      filtered.sort((a, b) => {
        const tierDiff = getTierPriority(a.tier) - getTierPriority(b.tier);
        if (tierDiff !== 0) return tierDiff;
        const unitDiff = getUnitPrice(a) - getUnitPrice(b);
        if (unitDiff !== 0) return unitDiff;
        return (a.name || '').localeCompare(b.name || '');
      });
    } else if (sortMode === 'best-value') {
      filtered.sort((a, b) => {
        const unitDiff = getUnitPrice(a) - getUnitPrice(b);
        if (unitDiff !== 0) return unitDiff;
        return getMinPrice(a) - getMinPrice(b);
      });
    } else if (sortMode === 'az') {
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortMode === 'lowest-price') {
      filtered.sort((a, b) => getMinPrice(a) - getMinPrice(b));
    }

    return filtered;
  };

  // ── PriceComparison screen ────────────────────────────────────────────────────
  if (selectedProduct) {
    const prices = (selectedProduct.prices || [])
      .filter(p => p.price > 0)
      .map(p => {
        const store = stores.find(s => s.id === p.storeId);
        const name  = store?.name || normStoreId(p.storeId).replace(/_/g, ' ');
        return { ...p, storeName: name };
      });
    const siblings = allProducts.filter(p => p.productId === selectedProduct.productId);

    return (
      <div className="browse-page">
        <PriceComparison
          product={selectedProduct}
          prices={prices}
          siblings={siblings}
          stores={stores}
          onBack={() => setSelectedProduct(null)}
          onAddToBasket={onAddToBasket}
          aisleContext={null}
          onBackToAisle={() => setSelectedProduct(null)}
        />
      </div>
    );
  }

  const filteredProducts     = getFilteredProducts(displayProducts);
  filteredCountRef.current   = filteredProducts.length;  // keep ref in sync for the effect
  const aisleSubcategories   = getSubcategoriesForAisle(selectedAisle);
  const currentAisle         = AISLES.find(a => a.id === selectedAisle);
  const searchTerm           = listQuery.trim();
  const isSearchMiss         = searchTerm !== '' && filteredProducts.length === 0;

  // ── Main view ─────────────────────────────────────────────────────────────────
  return (
    <div className="browse-page">
      <div className="browse-container">

        {/* ── Search ── */}
        <div className="ep-search-row">
          <div className="lv-search-wrap">
            <svg className="lv-search-icon" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="9" cy="9" r="6"/>
              <line x1="14" y1="14" x2="18" y2="18"/>
            </svg>
            <input
              type="text"
              className="lv-search-input"
              placeholder="Find a product"
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
            />
            {listQuery && (
              <button className="lv-search-clear" onClick={() => setListQuery('')} aria-label="Clear search">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                  <line x1="1" y1="1" x2="11" y2="11"/>
                  <line x1="11" y1="1" x2="1" y2="11"/>
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Shortcut row ── */}
        {shortcutRow.products.length > 0 && (
          <div className="ep-shortcut-section">
            <h2 className="ep-section-title">{shortcutRow.title}</h2>
            <div className="ep-shortcut-scroll-wrap">
              <div className="ep-shortcut-scroll">
                {shortcutRow.products.map(p => (
                  <ShortcutCard
                    key={p.id}
                    product={p}
                    stores={stores}
                    onSelect={handleSelectProduct}
                  />
                ))}
              </div>
              <div className="ep-scroll-fade" aria-hidden="true"/>
            </div>
          </div>
        )}

        {/* ── Aisles ── */}
        <div className="ep-aisles-section">
          <h2 className="ep-aisles-label">Aisles</h2>
          <div className="ep-aisles-scroll-wrap">
            <div className="ep-aisles-scroll">
              {/* All chip — always first, active when no aisle selected */}
              <button
                className={`ep-aisle-chip${selectedAisle === null ? ' active' : ''}`}
                onClick={() => handleAisleSelect(null)}
                aria-pressed={selectedAisle === null}
              >
                <span className="ep-aisle-name">All</span>
              </button>
              {AISLES.filter(a => !a.comingSoon).map(aisle => (
                <button
                  key={aisle.id}
                  className={`ep-aisle-chip${selectedAisle === aisle.id ? ' active' : ''}`}
                  onClick={() => handleAisleSelect(aisle.id)}
                  aria-pressed={selectedAisle === aisle.id}
                >
                  <span className="ep-aisle-icon" aria-hidden="true">{aisle.icon}</span>
                  <span className="ep-aisle-name">{aisle.name}</span>
                </button>
              ))}
            </div>
            <div className="ep-scroll-fade" aria-hidden="true"/>
          </div>
        </div>

        {/* ── Combined meta + controls row: "22 products • Filters • Sort" ── */}
        <div className="ep-controls-row">
          <span className="ep-count">
            {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
            {currentAisle ? ` · ${currentAisle.name}` : ''}
          </span>
          <span className="ep-controls-sep" aria-hidden="true">•</span>
          <button
            className={`ep-text-btn${hasActiveFilters ? ' active' : ''}`}
            onClick={openFilterSheet}
          >
            {activeFilterCount > 0 ? `Filters · ${activeFilterCount}` : 'Filters'}
          </button>
          <span className="ep-controls-sep" aria-hidden="true">•</span>
          <button
            className={`ep-text-btn${isSortModified ? ' active' : ''}`}
            onClick={() => setSortSheetOpen(true)}
          >
            Sort
          </button>
          {(hasActiveFilters || selectedAisle || listQuery) && filteredProducts.length > 0 && (
            <button className="lv-filter-clear" onClick={clearAll}>Clear all</button>
          )}
        </div>

        {/* ── Results grid (key triggers fade animation on aisle change) ── */}
        {filteredProducts.length > 0 ? (
          <div key={gridKey} className="ep-grid-animate">
            <ProductCardGrid
              products={filteredProducts}
              allProducts={allProducts}
              stores={stores}
              onSelectProduct={handleSelectProduct}
              onAddToBasket={onAddToBasket}
            />
          </div>
        ) : isSearchMiss ? (
          /* Case A — user typed a term and nothing matched: show request CTA */
          <div className="lv-empty">
            <p className="lv-empty-heading">
              No products found for "{searchTerm}"
            </p>
            {requestSubmitted ? (
              <p className="lv-confirmed-msg">We'll look into adding this. Thanks for the suggestion.</p>
            ) : (
              <div className="lv-empty-actions">
                <button
                  className="lv-request-btn"
                  onClick={() => setShowRequestModal(true)}
                >
                  Request this product →
                </button>
                <button className="lv-pill" onClick={clearAll}>
                  Clear search
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Case B — filters (no search term) caused zero results */
          <div className="lv-empty">
            <p className="lv-empty-heading">No products match your filters.</p>
            {(hasActiveFilters || selectedAisle) && (
              <button className="lv-pill" onClick={clearAll}>
                Clear all filters
              </button>
            )}
          </div>
        )}

      </div>

      {/* ── Sort sheet ───────────────────────────────────────────────────────────── */}
      {sortSheetOpen && (
        <div className="lv-sort-sheet" onClick={() => setSortSheetOpen(false)}>
          <div className="lv-sort-inner" onClick={e => e.stopPropagation()}>
            <div className="lv-sort-title">Sort by</div>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.id}
                className={`ep-sort-opt${sortMode === opt.id ? ' active' : ''}`}
                onClick={() => { setSortMode(opt.id); setSortSheetOpen(false); }}
              >
                <div>
                  <div className="lv-sort-opt-label">{opt.label}</div>
                  <div className="lv-sort-opt-sub">{opt.sub}</div>
                </div>
                {sortMode === opt.id && (
                  <span className="ep-sort-check" aria-hidden="true"><Check /></span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Request product modal ────────────────────────────────────────────────── */}
      <RequestModal
        isOpen={showRequestModal}
        initialProductName={searchTerm}
        showRequestType={false}
        onClose={() => setShowRequestModal(false)}
        onSuccess={() => {
          setShowRequestModal(false);
          setRequestSubmitted(true);
        }}
      />

      {/* ── Filters sheet ────────────────────────────────────────────────────────── */}
      {filterSheetOpen && (
        <div className="lv-sort-sheet" onClick={() => setFilterSheetOpen(false)}>
          <div className="lv-filter-inner" onClick={e => e.stopPropagation()}>

            <div className="lv-filter-titlebar">
              <span className="lv-filter-titlebar-title">
                Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
              </span>
              <button
                className="lv-filter-titlebar-close"
                onClick={() => setFilterSheetOpen(false)}
                aria-label="Close filters"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="1" y1="1" x2="11" y2="11"/>
                  <line x1="11" y1="1" x2="1" y2="11"/>
                </svg>
              </button>
            </div>

            <div className="lv-filter-scroll">

              {/* ── Subcategory (only when an aisle is selected on the main screen) ── */}
              {selectedAisle && aisleSubcategories.length > 0 && (
                <div className="lv-filter-section">
                  <div className="lv-filter-section-label">Subcategory</div>
                  <div className="ep-sheet-scroll-row">
                    <button
                      className={`ep-sheet-pill${!pendingFilters.subcategory ? ' active' : ''}`}
                      onClick={() => setPendingFilters(f => ({ ...f, subcategory: null }))}
                    >
                      All
                    </button>
                    {aisleSubcategories.map(subcatId => {
                      const display = getSubcategoryDisplay(subcatId);
                      return (
                        <button
                          key={subcatId}
                          className={`ep-sheet-pill${pendingFilters.subcategory === subcatId ? ' active' : ''}`}
                          onClick={() => setPendingFilters(f => ({
                            ...f,
                            subcategory: f.subcategory === subcatId ? null : subcatId,
                          }))}
                        >
                          {display.icon} {display.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Range (tier) ── */}
              <div className="lv-filter-section">
                <div className="lv-filter-section-label">Range</div>
                <div className="ep-sheet-scroll-row">
                  <button
                    className={`ep-sheet-pill${!pendingFilters.range ? ' active' : ''}`}
                    onClick={() => setPendingFilters(f => ({ ...f, range: null }))}
                  >
                    Any
                  </button>
                  {RANGES.map(range => (
                    <button
                      key={range.id}
                      className={`ep-sheet-pill${pendingFilters.range === range.id ? ' active' : ''}`}
                      onClick={() => setPendingFilters(f => ({
                        ...f,
                        range: f.range === range.id ? null : range.id,
                      }))}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Store ── */}
              <div className="lv-filter-section">
                <div className="lv-filter-section-label">Store</div>
                <div className="lv-tick-grid-2">
                  {stores.map(store => (
                    <button
                      key={store.id}
                      className={`lv-store-pill${pendingFilters.stores.includes(store.id) ? ' on' : ''}`}
                      onClick={() => togglePendingStore(store.id)}
                    >
                      <span className={`lv-pill-check${pendingFilters.stores.includes(store.id) ? ' on' : ''}`}>
                        {pendingFilters.stores.includes(store.id) && <Tick />}
                      </span>
                      <span className="lv-pill-name">{store.name}</span>
                    </button>
                  ))}
                </div>
                {pendingFilters.stores.length > 0 && (
                  <p className="lv-store-count">{pendingFilters.stores.length} selected</p>
                )}
              </div>

              {/* ── More options ── */}
              <div className="lv-filter-section lv-filter-section-last">
                <button className="lv-more-toggle" onClick={handleToggleMoreOptions}>
                  <span>More options</span>
                  <span className="lv-more-chevron">{showMoreOptions ? '▲' : '▼'}</span>
                </button>

                {showMoreOptions && (
                  <div className="lv-more-body">

                    <button
                      className="lv-more-row"
                      onClick={() => setPendingFilters(f => ({ ...f, ownBrand: !f.ownBrand }))}
                    >
                      <span className={`lv-pill-check${pendingFilters.ownBrand ? ' on' : ''}`}>
                        {pendingFilters.ownBrand && <Tick />}
                      </span>
                      <span className="lv-tick-label">Own brand only</span>
                    </button>

                    <button
                      className="lv-more-row"
                      onClick={() => setPendingFilters(f => ({ ...f, aldiMatch: !f.aldiMatch }))}
                    >
                      <span className={`lv-pill-check${pendingFilters.aldiMatch ? ' on' : ''}`}>
                        {pendingFilters.aldiMatch && <Tick />}
                      </span>
                      <span className="lv-tick-label">Aldi price match only</span>
                    </button>

                    <div className="lv-pack-section">
                      <span className="lv-pack-label">Pack size preference</span>
                      <div className="lv-pack-row">
                        {PACK_SIZES.map(ps => (
                          <button
                            key={ps.id}
                            className={`lv-store-pill lv-pack-pill${pendingFilters.packSizes.includes(ps.id) ? ' on' : ''}`}
                            onClick={() => setPendingFilters(f => {
                              const next = f.packSizes.includes(ps.id)
                                ? f.packSizes.filter(id => id !== ps.id)
                                : [...f.packSizes, ps.id];
                              return { ...f, packSizes: next };
                            })}
                          >
                            <span className={`lv-pill-check${pendingFilters.packSizes.includes(ps.id) ? ' on' : ''}`}>
                              {pendingFilters.packSizes.includes(ps.id) && <Tick />}
                            </span>
                            <span className="lv-pill-name">{ps.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      className="lv-more-row"
                      onClick={() => setPendingFilters(f => ({ ...f, recentOnly: !f.recentOnly }))}
                    >
                      <span className={`lv-pill-check${pendingFilters.recentOnly ? ' on' : ''}`}>
                        {pendingFilters.recentOnly && <Tick />}
                      </span>
                      <div className="lv-more-row-text">
                        <span className="lv-tick-label">Recent prices only</span>
                        <span className="lv-filter-note">Prices checked in the last 7 days</span>
                      </div>
                    </button>

                  </div>
                )}
              </div>

            </div>

            <div className="lv-filter-footer">
              <button
                className="lv-filter-clear-btn"
                onClick={() => setPendingFilters({ ...EMPTY_FILTERS })}
              >
                Clear all
              </button>
              <button className="lv-filter-apply-btn" onClick={applyFilters}>
                Apply filters
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}

export default ExplorePage;
